import React, { useImperativeHandle, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import {
    DatePicker,
    FlexboxGrid,
    Popover,
    Radio,
    RadioGroup,
    Schema,
    SelectPicker
} from "rsuite";

import { servicePlanNoTrunkFilter } from "utils/filters";
import { fixedFloat, servicePick } from "utils";
import BillingIncrement from "components/Filters/BillingIncrement";
import CheckboxControl from "components/Form/CheckboxControl";
import ServicePlans from "components/Filters/ServicePlans";
import CustomField from "components/Form/CustomField";
import Text, { str40 } from "components/Form/Text";
import Form from "components/Form/CompatForm";

import DialerFormSection from "./DialerFormSection";
import ExternalResourceSection from "./ExternalResourceSection";
import MoreOptionsFormSection from "./MoreOptionsFormSection";
import IvrFormSection from "./IvrFormSection";
import { buildMoreOptionsModel, formSmsModel } from "./TrunkFormRefactored.utils";
import {
    ResellerRatesRow,
    StyledColumn,
    StyledGrid,
    TermPointItem
} from "./TrunkFormRefactored.styles";

const { NumberType } = Schema.Types;

const toFormDate = (value) => {
    if (!value || value instanceof Date) {
        return value || null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

const TrunkFormRefactored = ({
    disabled,
    updateFormRef,
    updateFormRef2,
    updateFormRef3,
    updateFormRef4,
    termPoint,
    initialValues,
    defaultSPKey,
    newTrunk,
    isMobile,
    error,
    account,
    service,
    setError,
    ivr_list,
    accountServicePlanList = [],
    moreOptionsFlag,
    setMoreOptionsFlag,
    setMoreData,
    setTermPoint,
    externalAuthMode,
    setExternalAuthMode,
    onPostbackTest
}) => {
    const formRef = useRef(null);
    const formMoreModel = buildMoreOptionsModel(service);
    const isDefault = termPoint === "default";
    const isDialer = termPoint === "dialer";
    const isExternal = termPoint === "external";

    const [chosenSpKey, setChosenSpKey] = useState((initialValues && initialValues.sp_key) || 1);
    const [chosenRrcKey, setChosenRrcKey] = useState((initialValues && initialValues.rrc_key) || null);
    const [isResellerState, setIsResellerState] = useState((initialValues && initialValues.is_reseller) || null);

    const formModel = useMemo(() => {
        return Schema.Model({
            name: str40,
            sp_key: NumberType().isRequired("Required"),
            rrc_key: NumberType().isRequired("Required"),
            ...(!!isResellerState ? {
                subacc_rate_percent_month: NumberType().min(0, "Min number is 0").max(100, "Max number is 100").isRequired("Required"),
                subacc_rate_percent_week: NumberType().min(0, "Min number is 0").max(100, "Max number is 100").isRequired("Required")
            } : {})
        });
    }, [isResellerState]);

    const defaultFormValue = useMemo(() => {
        return {
            active: true,
            delivery_delay_type: 0,
            autorevoke: null,
            ...initialValues,
            start_date: toFormDate(initialValues?.start_date),
            end_date: toFormDate(initialValues?.end_date),
            termination_point: termPoint,
            dlr_percent: (!newTrunk ? initialValues?.dlr_percent || 0 : 0.97) * 100,
            ...(!!initialValues?.is_reseller ? {
                subacc_rate_percent_month: fixedFloat((initialValues?.subacc_rate_percent_month || 0) * 100),
                subacc_rate_percent_week: fixedFloat((initialValues?.subacc_rate_percent_week || 0) * 100)
            } : {})
        };
    }, [initialValues, newTrunk, termPoint]);

    const [formValue, setFormValue] = useState(defaultFormValue);

    useImperativeHandle(updateFormRef, () => ({
        check: (...args) => formRef.current?.check(...args),
        getFormValue: () => formValue,
        root: formRef.current?.root || null
    }), [formValue]);

    const excludedSpList = useMemo(() => {
        if (initialValues.id) {
            return [];
        }
        return accountServicePlanList.filter(item => !item.allowed).map(item => item.sp_key);
    }, [initialValues, accountServicePlanList]);

    const formModelValue = service ? formModel : formSmsModel;

    const voiceParametersInfo = (
        <Popover title="Parameters">
            <p>Our ip for postback is 51.38.107.49</p>
            <p><b>{"{{cdrid}}"}</b> - Call id</p>
            <p><b>{"{{calling_number}}"}</b> - A-number</p>
            <p><b>{"{{called_number}}"}</b> - B-number</p>
            <p><b>{"{{rate}}"}</b> - Terminator rate</p>
            <p><b>{"{{duration}}"}</b> - Tariffed call duration </p>
            <p><b>{"{{payout}}"}</b> - Cost per side of the terminator</p>
            <p><b>{"{{calltime}}"}</b> - Call initialization time </p>
            <p><b>{"{{additional_tones}}"}</b> - Additional tones </p>
        </Popover>
    );

    const smsParametersInfo = (
        <Popover title="Parameters">
            <p>Our ip for postback is 51.38.107.49</p>
            <p><b>{"{{smsid}}"}</b> - SMS id</p>
            <p><b>{"{{smsid2}}"}</b> - SMS id 2</p>
            <p><b>{"{{senderid}}"}</b> - Sender id</p>
            <p><b>{"{{smstime}}"}</b> - SMS time</p>
            <p><b>{"{{payout}}"}</b> - Payout</p>
            <p><b>{"{{called_number}}"}</b> - Called number</p>
            <p><b>{"{{smstext}}"}</b> - SMS text</p>
        </Popover>
    );

    return (
        <Form
            layout={isMobile ? null : "inline"}
            model={formModelValue}
            formDefaultValue={defaultFormValue}
            formValue={formValue}
            onChange={setFormValue}
            ref={formRef}
            style={{ margin: 0, width: "100%" }}
        >
            <FlexboxGrid
                align={isMobile ? "flex-start" : "center"}
                style={{ margin: 0, width: "100%" }}
            >
                <FlexboxGrid.Item className={"mb-2"}>
                    <Text
                        name="name"
                        placeholder="Name"
                        style={{ width: 200, marginLeft: 0 }}
                    />
                    <ServicePlans
                        classPrefix={"rs-form-control"}
                        excluded={excludedSpList}
                        disabled={!!initialValues.id}
                        style={{ verticalAlign: "none", marginLeft: 10, paddingRight: 10, height: 36 }}
                        filterFunc={servicePlanNoTrunkFilter}
                        onChange={(value) => {
                            if (chosenSpKey === value) return;
                            setChosenSpKey(value);
                        }}
                    />
                    <CheckboxControl
                        name="active"
                        defaultChecked={defaultFormValue.active}
                    >
                        Active
                    </CheckboxControl>

                    {service && account.is_reseller && <CheckboxControl
                        name="is_reseller"
                        defaultChecked={false}
                        disabled={initialValues.id}
                        checked={initialValues.is_reseller}
                        onChange={(value) => {
                            setIsResellerState(value);
                        }}
                    >
                        Reseller trunk
                    </CheckboxControl>}
                </FlexboxGrid.Item>
            </FlexboxGrid>

            <StyledGrid
                isMobile={isMobile}
                align={"flex-start"}
                style={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: 0, }}
            >
                {service ? <FlexboxGrid.Item className={!isMobile ? "mr-4" : ""}>
                    <BillingIncrement
                        classPrefix={"form-control"}
                        style={isMobile
                            ? { verticalAlign: "none", height: 36 }
                            : { verticalAlign: "none", marginLeft: 10, paddingRight: 10, height: 36 }
                        }
                        onChange={(value) => {
                            if (chosenRrcKey === value) return;
                            setChosenRrcKey(value);
                        }}
                        label="Billing Increment"
                    />
                </FlexboxGrid.Item> : <></>}
                <FlexboxGrid.Item className={!isMobile ? "mr-4" : ""}>
                    <CustomField
                        name="start_date"
                        placeholder="Start Date"
                        placement="autoVerticalEnd"
                        cleanable={false}
                        accepter={DatePicker}
                        style={{ width: 165 }}
                        ranges={[
                            {
                                label: "Today",
                                value: new Date()
                            },
                            {
                                label: "Tomorrow",
                                value: new Date(new Date().setDate(new Date().getDate() + 1))
                            }
                        ]}
                        inline={isMobile}
                        oneTap={isMobile}
                        label={servicePick(service, "", "Start Date")}
                    />
                </FlexboxGrid.Item>
                <FlexboxGrid.Item>
                    {isMobile && !initialValues.id
                        ? null
                        : <CustomField
                            name="end_date"
                            placeholder="End Date"
                            placement="autoVerticalEnd"
                            accepter={DatePicker}
                            style={{ width: 165 }}
                            disabled={!initialValues.id}
                            ranges={[
                                {
                                    label: "Today",
                                    value: new Date()
                                },
                                {
                                    label: "Tomorrow",
                                    value: new Date(new Date().setDate(new Date().getDate() + 1))
                                }
                            ]}
                            inline={isMobile}
                            oneTap={isMobile}
                            label={servicePick(service, "", "End Date")}
                        />}
                </FlexboxGrid.Item>
                {!!isResellerState && <ResellerRatesRow isMobile={isMobile}>
                    <FlexboxGrid.Item style={{ marginRight: "10px", marginTop: "10px" }}>
                        <CustomField
                            name="subacc_rate_percent_month"
                            placeholder="Monthly rate percent"
                            label="Reseller's"
                            width={100}
                        />
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item style={{ marginRight: "10px", marginTop: "10px" }}>
                        <CustomField
                            name="subacc_rate_percent_week"
                            placeholder="Weekly rate percent"
                            width={100}
                        />
                    </FlexboxGrid.Item>
                </ResellerRatesRow>}
            </StyledGrid>

            <FlexboxGrid.Item style={{ marginTop: "10px" }}>
                <AutorevokeRow
                    isMobile={isMobile}
                    align={isMobile ? "flex-start" : "center"}
                >
                    <CustomField
                        accepter={SelectPicker}
                        name="autorevoke"
                        data={[
                            { label: "No", value: false },
                            { label: "Yes", value: true },
                            { label: "Default", value: null }
                        ]}
                        placeholder="Autorevoke"
                        defaultValue={null}
                        label="Autorevoke"
                        width={155}
                        searchable={false}
                    />
                </AutorevokeRow>
            </FlexboxGrid.Item>

            <StyledColumn
                align={isMobile ? "flex-start" : "center"}
                style={{ margin: 0, width: "100%" }}
            >
                {service ? <TermPointItem>
                    <CustomField
                        disabled={disabled}
                        accepter={TermPointRadioGroup}
                        onChange={setTermPoint}
                        name="termination_point"
                        style={{ marginLeft: "-10px" }}
                    >
                        <Radio value="default">Default termination point</Radio>
                        <Radio value="dialer">Dialer panel</Radio>
                        <Radio value="external">External resource</Radio>
                    </CustomField>
                </TermPointItem> : <></>}
                {(isDefault || isDialer) && <FlexboxGrid.Item>
                    <DialerFormSection
                        initialValues={initialValues}
                        disabled={disabled}
                        formRef={updateFormRef2}
                        defaultSPKey={defaultSPKey}
                        showOnlyMoreOption={isDefault}
                        moreOptionsFlag={moreOptionsFlag}
                        setMoreOptionsFlag={setMoreOptionsFlag}
                    />
                </FlexboxGrid.Item>}

                {isExternal && <FlexboxGrid.Item style={isMobile ? { width: "100%" } : { minWidth: 540 }}>
                    <ExternalResourceSection
                        initialValues={initialValues}
                        disabled={disabled}
                        formRef={updateFormRef4}
                        authMode={externalAuthMode}
                        setAuthMode={setExternalAuthMode}
                    />
                </FlexboxGrid.Item>}

                {moreOptionsFlag && <FlexboxGrid.Item>
                    <MoreOptionsFormSection
                        error={error}
                        setError={setError}
                        disabled={disabled}
                        model={formMoreModel}
                        setMoreData={setMoreData}
                        initialValues={initialValues}
                        parametersInfo={servicePick(service, voiceParametersInfo, smsParametersInfo)}
                        service={service}
                        isDefault={isDefault}
                        onPostbackTest={onPostbackTest}
                    />
                </FlexboxGrid.Item>}
                {service && isDefault && <FlexboxGrid.Item style={isMobile ? { width: "100%" } : { minWidth: 400 }}>
                    <IvrFormSection
                        isMobile={isMobile}
                        ivr_list={ivr_list}
                        initialValues={initialValues}
                        disabled={disabled}
                        formRef={updateFormRef3}
                    />
                </FlexboxGrid.Item>}
            </StyledColumn>

            {
                !service && <FlexboxGrid
                    align={isMobile ? "flex-start" : "center"}
                    style={{
                        ...(isMobile ? {
                            margin: 0,
                        } : {
                            margin: 0,
                            marginLeft: -65
                        }),
                        width: "100%"
                    }}
                >
                    <FlexboxGrid.Item>
                        <CheckboxControl
                            name="full_message"
                            defaultChecked={defaultFormValue.full_message}
                        >
                            Show non-anonymized messages on this trunk
                        </CheckboxControl>
                    </FlexboxGrid.Item>
                </FlexboxGrid>
            }
        </Form >
    );
};

const mapState = ({ references }) => ({
    defaultSPKey: references.defaultSPKey
});

export default connect(mapState, null)(TrunkFormRefactored);

const AutorevokeRow = styled(StyledGrid)`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const TermPointRadioGroup = styled(RadioGroup).attrs({ inline: true })`
    && {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
    }

    && .rs-radio {
        flex: 0 0 auto;
    }
`;
